import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

// Generic sortable, per-column-filterable, paginated table. Pages pass typed
// rows + a column spec; QueryInspector's raw-rows tab passes record-shaped
// rows derived from the SQL query result. One implementation, two consumers.

export type ColumnSpec<T> = {
  key: string;
  label: string;
  // Cell renderer. Receives the row plus the raw value at `key`.
  render?: (row: T, value: unknown) => ReactNode;
  // How to read the underlying value. Defaults to `(row as any)[key]`.
  accessor?: (row: T) => unknown;
  // Filterable columns expose a multi-select chip in the header.
  filterable?: boolean;
  // Sortable columns toggle on click. Default true.
  sortable?: boolean;
  // Right-align numeric columns.
  align?: "left" | "right";
  defaultWidth?: number;
  minWidth?: number;
  tooltip?: string;
};

type SortDir = "asc" | "desc";

const DEFAULT_PAGE_SIZE = 100;

export function FilterableTable<T>({
  rows,
  columns,
  pageSize = DEFAULT_PAGE_SIZE,
  initialSortKey,
  initialSortDir = "desc",
  searchAccessor,
  searchPlaceholder = "Search",
  toolbar,
  rowKey,
  onRowClick,
  highlightedRowKey,
  emptyMessage = "No rows match your filters.",
  renderExpandedRow,
}: {
  rows: T[];
  columns: ColumnSpec<T>[];
  pageSize?: number;
  initialSortKey?: string;
  initialSortDir?: SortDir;
  searchAccessor?: (row: T) => string;
  searchPlaceholder?: string;
  toolbar?: ReactNode;
  rowKey: (row: T, idx: number) => string;
  onRowClick?: (row: T) => void;
  highlightedRowKey?: string | null;
  emptyMessage?: string;
  // When provided, each row gets a leading chevron cell and expanding the row
  // renders the result of this callback in a full-width row beneath it.
  renderExpandedRow?: (row: T) => ReactNode;
}) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const expandable = !!renderExpandedRow;
  const totalCols = columns.length + (expandable ? 1 : 0);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initialSortDir);
  const [page, setPage] = useState(0);
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({});

  const accessor = useMemo(() => {
    const map: Record<string, (row: T) => unknown> = {};
    for (const c of columns) {
      map[c.key] = c.accessor ?? ((row: T) => (row as Record<string, unknown>)[c.key]);
    }
    return map;
  }, [columns]);

  const filterOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of columns) {
      if (!c.filterable) continue;
      const set = new Set<string>();
      const get = accessor[c.key];
      if (!get) continue;
      for (const r of rows) {
        const v = get(r);
        const s = v == null ? "" : String(v);
        if (s) set.add(s);
      }
      out[c.key] = Array.from(set).sort();
    }
    return out;
  }, [rows, columns, accessor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const active = Object.entries(colFilters)
      .map(([k, vals]) => (vals && vals.length > 0 ? { key: k, set: new Set(vals) } : null))
      .filter((x): x is { key: string; set: Set<string> } => x !== null);
    return rows.filter((r) => {
      for (const { key, set } of active) {
        const get = accessor[key];
        const v = get ? get(r) : undefined;
        if (!set.has(v == null ? "" : String(v))) return false;
      }
      if (q && searchAccessor) {
        if (!searchAccessor(r).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, colFilters, accessor, searchAccessor]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const get = accessor[sortKey];
    if (!get) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const na = typeof va === "number" ? va : Number(va);
      const nb = typeof vb === "number" ? vb : Number(vb);
      if (Number.isFinite(na) && Number.isFinite(nb)) {
        if (na < nb) return -1 * dir;
        if (na > nb) return 1 * dir;
        return 0;
      }
      const sa = String(va);
      const sb = String(vb);
      return sa < sb ? -1 * dir : sa > sb ? 1 * dir : 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir, accessor]);

  useEffect(() => {
    setPage(0);
  }, [search, colFilters, sortKey, sortDir, highlightedRowKey]);

  // Pin highlighted row to the very top regardless of sort/filter, so users
  // can scan it across panels without scrolling or paging.
  const displayed = useMemo(() => {
    if (!highlightedRowKey) return sorted;
    const idx = sorted.findIndex((r, i) => rowKey(r, i) === highlightedRowKey);
    if (idx < 0) return sorted;
    const head = sorted[idx]!;
    return [head, ...sorted.slice(0, idx), ...sorted.slice(idx + 1)];
  }, [sorted, highlightedRowKey, rowKey]);

  const pageCount = Math.max(1, Math.ceil(displayed.length / pageSize));
  const paginated = useMemo(
    () => displayed.slice(page * pageSize, (page + 1) * pageSize),
    [displayed, page, pageSize],
  );

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <div className="filterable-table">
      <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {searchAccessor && (
          <span className="input-wrap">
            <i className="ti ti-search" />
            <input
              type="text"
              className="input"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </span>
        )}
        <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
          {sorted.length.toLocaleString()} rows
        </span>
        <span style={{ flex: 1 }} />
        {toolbar}
      </div>
      <div className="table-wrap">
        <table className="t" style={{ tableLayout: "auto", width: "100%" }}>
          <thead>
            <tr>
              {expandable && <th style={{ width: 28 }} aria-label="Expand" />}
              {columns.map((c) => {
                const sortable = c.sortable !== false;
                const active = sortKey === c.key;
                const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                const selected = colFilters[c.key] ?? [];
                return (
                  <th
                    key={c.key}
                    title={c.tooltip}
                    style={{ textAlign: c.align ?? "left", position: "relative", minWidth: c.minWidth }}
                  >
                    <span
                      className={sortable ? "th-label" : "th-label no-sort"}
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      style={{ cursor: sortable ? "pointer" : "default" }}
                    >
                      {c.label}{arrow}
                    </span>
                    {c.filterable && (
                      <ColFilter
                        label={c.label}
                        options={filterOptions[c.key] ?? []}
                        selected={selected}
                        onChange={(next) => setColFilters((prev) => ({ ...prev, [c.key]: next }))}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: 16, color: "var(--fg-3)" }}>{emptyMessage}</td></tr>
            ) : paginated.flatMap((r, i) => {
              const key = rowKey(r, i);
              const isHi = highlightedRowKey === key;
              const isOpen = expandable && expandedKeys.has(key);
              const mainRow = (
                <tr
                  key={key}
                  className={isHi ? "is-highlighted" : ""}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  style={{ cursor: onRowClick ? "pointer" : undefined }}
                >
                  {expandable && (
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        });
                      }}
                      style={{ width: 28, textAlign: "center", cursor: "pointer", color: "var(--fg-3)" }}
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      <i className={`ti ${isOpen ? "ti-chevron-down" : "ti-chevron-right"}`} />
                    </td>
                  )}
                  {columns.map((c) => {
                    const get = accessor[c.key];
                    const v = get ? get(r) : undefined;
                    return (
                      <td key={c.key} style={{ textAlign: c.align ?? "left" }}>
                        {c.render ? c.render(r, v) : (v == null ? "" : String(v))}
                      </td>
                    );
                  })}
                </tr>
              );
              if (!isOpen || !renderExpandedRow) return [mainRow];
              return [
                mainRow,
                <tr key={`${key}__expanded`} className="is-expanded-row">
                  <td colSpan={totalCols} style={{ padding: 0, background: "var(--bg-subtle, #FAFAF7)" }}>
                    {renderExpandedRow(r)}
                  </td>
                </tr>,
              ];
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="pagination-bar">
          <button className="btn btn-sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</button>
          <span className="pagination-info">Page {page + 1} of {pageCount} ({sorted.length.toLocaleString()} rows)</span>
          <button className="btn btn-sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}>Next</button>
        </div>
      )}
    </div>
  );
}

function ColFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  const active = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (v: string, multi: boolean) => {
    if (multi) {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    } else {
      onChange(selected.length === 1 && selected[0] === v ? [] : [v]);
    }
  };

  return (
    <span
      ref={ref}
      style={{ position: "relative", display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={`Filter ${label}`}
        title={active ? `${label} filtered (${selected.length})` : `Filter ${label}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent", border: "none", padding: 2, cursor: "pointer",
          color: active ? "var(--color-teal)" : "var(--fg-3)",
          display: "inline-flex", alignItems: "center", fontSize: 13,
        }}
      >
        <i className={active ? "ti ti-filter-filled" : "ti ti-filter"} />
      </button>
      {open && (
        <div className="month-multi-popover" role="listbox" onClick={(e) => e.stopPropagation()}>
          <label className="row">
            <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} />
            <span>All</span>
          </label>
          <div className="divider" />
          <span className="multi-hint">Shift+click to select multiple</span>
          {options.map((o) => (
            <div
              key={o}
              className="row"
              onClick={(e) => toggle(o, e.shiftKey)}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              <input
                type="checkbox"
                checked={selected.includes(o)}
                readOnly
                tabIndex={-1}
                style={{ pointerEvents: "none" }}
              />
              <span>{o}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
